import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {StatsBlockHorizontal} from 'shells/metrics/stats-block-horizontal';
import {Percentage} from 'shells/percentage/percentage';

import type {
    GetAudienceEngagementStats as GetAudienceEngagementStatsQuery,
    GetAudienceEngagementStatsVariables as GetAudienceEngagementStatsQueryVariables,
} from 'nutshell-graphql-types';

import {AudienceBounceModal} from '../../tooltip-modals/audience-bounce-modal';
import {AudienceEngagementModal} from '../../tooltip-modals/audience-engagement-modal';

import GET_AUDIENCE_MEMBERSHIP_STATS from '../graphql/queries/get-audience-engagement-stats.graphql';

import {ChannelCampaignBreakdown} from './channel-campaign-breakdown';

import styles from './audience-engagement-stats.module.css';

type Props = {
    audienceId: string;
};

export function AudienceEngagementStats(props: Props) {
    const {data, loading: isLoading} = useQuery<
        GetAudienceEngagementStatsQuery,
        GetAudienceEngagementStatsQueryVariables
    >(GET_AUDIENCE_MEMBERSHIP_STATS, {
        variables: {audienceId: props.audienceId},
    });

    const engagementStats =
        data && data.audience && data.audience.engagementStats
            ? data.audience.engagementStats
            : undefined;

    if (NutClientConfig.hasSmsAudiencesFF) {
        return <ChannelCampaignBreakdown data={data} isLoading={isLoading} />;
    }

    return (
        <div className={styles['audience-engagement-stats']}>
            <StatsBlockHorizontal
                stats={[
                    {
                        key: '1',
                        // @ts-expect-error
                        title: 'Messages delivered',
                        isLoading: isLoading,
                        value: engagementStats
                            ? engagementStats.totalSentCount.toLocaleString()
                            : null,
                        secondaryValue: engagementStats
                            ? {
                                  value: engagementStats.totalSentCount,
                                  nounSingular: 'message',
                                  nounPlural: 'messages',
                              }
                            : undefined,
                        modalComponent: AudienceEngagementModal,
                    },
                    {
                        key: '2',
                        // @ts-expect-error
                        title: 'Open rate',
                        isLoading: isLoading,
                        value: (
                            // @ts-expect-error
                            <Percentage
                                value={engagementStats ? engagementStats.openRate : null}
                                precision={2}
                            />
                        ),
                        secondaryValue: engagementStats
                            ? {
                                  value: engagementStats.openCount,
                                  nounSingular: 'open',
                                  nounPlural: 'opens',
                              }
                            : undefined,
                        modalComponent: AudienceEngagementModal,
                    },
                    {
                        key: '3',
                        // @ts-expect-error
                        title: 'Click rate',
                        isLoading: isLoading,
                        value: (
                            // @ts-expect-error
                            <Percentage
                                value={engagementStats ? engagementStats.clickRate : null}
                                precision={2}
                            />
                        ),
                        secondaryValue: engagementStats
                            ? {
                                  value: engagementStats.clickCount,
                                  nounSingular: 'click',
                                  nounPlural: 'clicks',
                              }
                            : undefined,
                        modalComponent: AudienceEngagementModal,
                    },
                    {
                        key: '4',
                        // @ts-expect-error
                        title: 'Bounce rate',
                        isLoading: isLoading,
                        value: (
                            // @ts-expect-error
                            <Percentage
                                value={engagementStats ? engagementStats.permanentBounceRate : null}
                                precision={2}
                            />
                        ),
                        secondaryValue: engagementStats
                            ? {
                                  value: engagementStats.permanentBounceCount,
                                  nounSingular: 'bounce',
                                  nounPlural: 'bounces',
                              }
                            : undefined,
                        modalComponent: AudienceBounceModal,
                    },
                ]}
                isVertical={true}
            />
        </div>
    );
}
